import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query'
import { platformApi } from './http'
import type {
  CreateSchoolPayload,
  Invitation,
  PlatformOverview,
  PlatformSchool,
  PlatformSchoolDetail,
  SchoolStatus,
  SiteSettings,
} from './types'

export const platformKeys = {
  all: ['platform'] as const,
  overview: () => [...platformKeys.all, 'overview'] as const,
  schools: (params: object) => [...platformKeys.all, 'schools', params] as const,
  school: (id: number) => [...platformKeys.all, 'school', id] as const,
  settings: () => [...platformKeys.all, 'settings'] as const,
}

export const settingsQuery = () =>
  queryOptions({
    queryKey: platformKeys.settings(),
    queryFn: () => platformApi.get<SiteSettings>('/settings'),
  })

export const overviewQuery = () =>
  queryOptions({
    queryKey: platformKeys.overview(),
    queryFn: () => platformApi.get<PlatformOverview>('/overview'),
  })

export const schoolsQuery = (params: { search?: string; status?: SchoolStatus; page: number }) =>
  queryOptions({
    queryKey: platformKeys.schools(params),
    queryFn: () => platformApi.page<PlatformSchool>('/schools', { ...params, perPage: 20 }),
    placeholderData: keepPreviousData,
  })

export const schoolQuery = (id: number) =>
  queryOptions({
    queryKey: platformKeys.school(id),
    queryFn: () => platformApi.get<PlatformSchoolDetail>(`/schools/${id}`),
  })

function usePlatformMutation<TVars, TResult>(fn: (vars: TVars) => Promise<TResult>) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: fn,
    onSuccess: () => void qc.invalidateQueries({ queryKey: platformKeys.all }),
  })
}

export const useCreateSchool = () =>
  usePlatformMutation((body: CreateSchoolPayload) =>
    platformApi.post<{ school: PlatformSchool; invitation: Invitation }>('/schools', body),
  )
export const useSuspendSchool = (id: number) =>
  usePlatformMutation((reason: string | null) =>
    platformApi.post<PlatformSchoolDetail>(`/schools/${id}/suspend`, { reason }),
  )
export const useReactivateSchool = (id: number) =>
  usePlatformMutation(() => platformApi.post<PlatformSchoolDetail>(`/schools/${id}/reactivate`))
export const useSetModules = (id: number) =>
  usePlatformMutation((modules: string[]) =>
    platformApi.put<PlatformSchoolDetail>(`/schools/${id}/modules`, { modules }),
  )
export const useSetAddress = (id: number) =>
  usePlatformMutation((subdomain: string | null) =>
    platformApi.put<PlatformSchoolDetail>(`/schools/${id}/address`, { subdomain }),
  )
export const useSaveSettings = () =>
  usePlatformMutation(
    (body: { baseDomain: string | null; contactEmail: string | null; contactPhone: string | null }) =>
      platformApi.put<SiteSettings>('/settings', body),
  )
export const useReinvite = (id: number) =>
  usePlatformMutation(() => platformApi.post<Invitation>(`/schools/${id}/invitation`))
